import { ServicePageTemplate, CapabilitiesGrid, BusinessImpactSection, WhyChooseUsSection, FAQSection } from "@/components/templates";
import { createServiceSchema, createFAQSchema } from "@/utils/structuredData";
import {
  Database,
  BarChart,
  Zap,
  Cloud,
  Cpu,
  Layers,
  Clock,
  Award,
  CheckCircle2,
  Users,
  TrendingUp,
  ShoppingCart,
  Landmark,
  HeartPulse,
  Factory,
  LineChart,
  PieChart,
  Workflow,
  Brain,
} from "lucide-react";
import { DoubleDiamondProcess } from "@/components/DoubleDiamondProcess";
import { HelpCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

// FAQ items for Data Visualisation
const dataFaqItems = [
  {
    question: "What BI tools do you work with?",
    answer: "We're experts in all major business intelligence platforms including Tableau, Power BI, Looker, and Metabase. We help you choose the right tool based on your organization's needs, existing infrastructure, and budget. We also build custom dashboards when off-the-shelf solutions don't fit.",
  },
  {
    question: "How do you handle data from multiple sources?",
    answer: "We specialize in connecting and unifying data from 50+ sources including databases, SaaS applications, APIs, and spreadsheets. Using ETL/ELT tools like Airflow, dbt, and Fivetran, we create a single source of truth for your entire organization.",
  },
  {
    question: "Can you build real-time dashboards?",
    answer: "Yes! We build real-time analytics solutions using stream processing technologies like Apache Kafka and Flink. Your dashboards can update in seconds rather than hours, enabling faster decision-making based on current data.",
  },
  {
    question: "Do you provide training for our team?",
    answer: "Training is included with every engagement. We ensure your team can use the dashboards, create their own reports, and maintain the data infrastructure. Self-service analytics is a key goal — reducing dependency on technical teams.",
  },
  {
    question: "What data warehousing solutions do you recommend?",
    answer: "We work with modern cloud data warehouses including Snowflake, BigQuery, Redshift, and Databricks. The right choice depends on your data volume, query patterns, and existing cloud infrastructure. We'll help you evaluate options.",
  },
  {
    question: "Can you help with predictive analytics and ML?",
    answer: "Absolutely! We build machine learning models for forecasting, anomaly detection, churn prediction, and recommendations. We use Python, TensorFlow, and MLflow to create predictive capabilities that go beyond traditional reporting.",
  },
];

// Data capabilities
const dataCapabilities = [
  {
    title: "Interactive Dashboards",
    description: "Real-time dashboards that update automatically. Drill-down capabilities, filters, and customizable views for every stakeholder.",
    icon: BarChart,
    examples: ["Executive Dashboards", "KPI Tracking", "Real-time Metrics", "Custom Filters", "Mobile-friendly"],
    gradient: "from-emerald-500 to-teal-500",
  },
  {
    title: "Data Warehousing",
    description: "Centralized data repositories with Snowflake, Redshift, and BigQuery. Unified analytics from all your data sources.",
    icon: Database,
    examples: ["Snowflake", "AWS Redshift", "BigQuery", "Azure Synapse", "Data Modeling"],
    gradient: "from-blue-500 to-cyan-500",
  },
  {
    title: "ETL/ELT Pipelines",
    description: "Automated data pipelines that extract, transform, and load data reliably. Real-time and batch processing at any scale.",
    icon: Workflow,
    examples: ["Apache Airflow", "dbt", "Fivetran", "Custom ETL", "Data Quality"],
    gradient: "from-violet-500 to-purple-500",
  },
  {
    title: "Real-time Analytics",
    description: "Stream processing with Kafka and Flink for instant insights. Make decisions based on data that's seconds old, not hours.",
    icon: Zap,
    examples: ["Apache Kafka", "Flink", "Real-time Alerts", "Event Streaming", "CDC"],
    gradient: "from-orange-500 to-red-500",
  },
  {
    title: "Business Intelligence",
    description: "Self-service BI tools that empower business users. Tableau, Power BI, and Looker implementations with training included.",
    icon: PieChart,
    examples: ["Tableau", "Power BI", "Looker", "Metabase", "Self-service BI"],
    gradient: "from-pink-500 to-rose-500",
  },
  {
    title: "Predictive Analytics",
    description: "Machine learning models that forecast trends, detect anomalies, and power data-driven recommendations.",
    icon: Brain,
    examples: ["Forecasting", "Anomaly Detection", "Churn Prediction", "Recommendations", "ML Pipelines"],
    gradient: "from-indigo-500 to-blue-500",
  },
];

// Data impact stats
const dataImpact = [
  {
    title: "Faster Decisions",
    description: "Replace weekly reports with real-time dashboards. Decision-makers get insights in seconds, not days.",
    icon: Zap,
    stat: "10x",
    statLabel: "Query Speed",
  },
  {
    title: "Unified Data",
    description: "Break down data silos. Connect all your sources into a single source of truth for the entire organization.",
    icon: Database,
    stat: "100+",
    statLabel: "Data Sources",
  },
  {
    title: "Self-Service Analytics",
    description: "Empower business users to explore data without waiting for engineering. Reduce dependency on technical teams.",
    icon: Users,
    stat: "80%",
    statLabel: "Less IT Dependency",
  },
];

const DataVisualisation = () => {
  const navigate = useNavigate();
  const serviceSchema = createServiceSchema({
    name: "Data Visualisation & Analytics Services",
    description:
      "Transform raw data into actionable insights with interactive dashboards, data warehousing, ETL pipelines, real-time analytics, and business intelligence solutions. Tableau, Power BI, Snowflake, and custom data platforms.",
    url: "https://interestech.com/services/data-visualisation",
    serviceType: "Data Analytics & Visualisation Services",
    areaServed: "Worldwide",
  });

  // FAQ Schema for rich snippets
  const faqSchema = createFAQSchema(dataFaqItems);

  return (
    <ServicePageTemplate
      seo={{
        title: "Data Visualisation & Analytics | Dashboards, BI & Data Warehousing | Interesting Technologies",
        description:
          "Transform raw data into actionable insights: interactive dashboards, data warehousing (Snowflake, BigQuery), ETL pipelines, real-time analytics, and BI solutions with Tableau & Power BI. Make data-driven decisions faster.",
        keywords:
          "data visualisation, data visualization, interactive dashboards, business intelligence, data analytics, Tableau, Power BI, Looker, data warehousing, Snowflake, BigQuery, ETL pipelines, real-time analytics, data engineering, KPI dashboards, reporting solutions",
        canonical: "https://interestech.com/services/data-visualisation",
        structuredData: [serviceSchema, faqSchema],
        ogImage: "https://interestech.com/itpl-og.webp",
        ogTitle: "Data Visualisation & Analytics | Dashboards & BI | Interesting Technologies",
        ogDescription: "Interactive dashboards, data warehousing, ETL pipelines & BI solutions. Transform raw data into actionable insights with Tableau, Power BI & custom platforms.",
      }}
      hero={{
        badge: { icon: BarChart, text: "Data-Driven Decisions" },
        title: "Turn Raw Data Into",
        titleHighlight: "Actionable Insights",
        description:
          "Interactive dashboards. Real-time analytics. Unified data warehouses. We build data platforms that transform how your organization makes decisions — from executive KPIs to operational metrics.",
        primaryCTA: { text: "Build Your Data Platform", onClick: () => navigate('/contact') },
        secondaryCTA: { text: "See Examples", onClick: () => navigate('/our-work') },
        gradient: "from-emerald-900 via-teal-800 to-cyan-900",
        accentColor: "green",
        orbitIcons: [BarChart, Database, PieChart, Zap, LineChart, Brain],
      }}
      stats={{
        items: [
          { label: "Dashboards Built", value: 500, suffix: "+", icon: BarChart },
          { label: "Data Sources Connected", value: 50, suffix: "+", icon: Database },
          { label: "Query Speed Improvement", value: 10, suffix: "x", icon: Zap },
          { label: "Client Satisfaction", value: 98, suffix: "%", icon: Award },
        ],
        gradient: "from-emerald-500 to-teal-500",
      }}
      features={{
        title: "Complete Data & Analytics Services",
        subtitle: "From raw data to executive dashboards — we build the entire data stack that powers data-driven organizations.",
        badge: { icon: Layers, text: "Data Expertise", color: "emerald" },
        items: [
          { 
            text: "Interactive Dashboards", 
            description: "Real-time dashboards with drill-downs, filters, and mobile support. Tableau, Power BI, or custom solutions.",
            icon: BarChart, 
            gradient: "from-emerald-500 to-teal-500" 
          },
          { 
            text: "Data Warehousing", 
            description: "Centralized data with Snowflake, BigQuery, or Redshift. Unified analytics from all your data sources.",
            icon: Database, 
            gradient: "from-blue-500 to-cyan-500" 
          },
          { 
            text: "ETL/ELT Pipelines", 
            description: "Automated data pipelines with Airflow, dbt, and Fivetran. Reliable data that's always fresh.",
            icon: Workflow, 
            gradient: "from-violet-500 to-purple-500" 
          },
          { 
            text: "Real-time Analytics", 
            description: "Stream processing with Kafka and Flink for instant insights. React to events as they happen.",
            icon: Zap, 
            gradient: "from-orange-500 to-red-500" 
          },
          { 
            text: "Self-Service BI", 
            description: "Empower business users with self-service tools. Less waiting, more exploring, faster decisions.",
            icon: PieChart, 
            gradient: "from-pink-500 to-rose-500" 
          },
          { 
            text: "Predictive Analytics", 
            description: "ML models for forecasting, anomaly detection, and recommendations. Data that predicts the future.",
            icon: Brain, 
            gradient: "from-indigo-500 to-blue-500" 
          },
        ],
      }}
      technologies={{
        title: "Data Technologies We Master",
        subtitle: "Modern data stack tools for reliable, scalable analytics platforms.",
        badge: { icon: Cpu, text: "Tech Stack", color: "emerald" },
        items: [
          { 
            name: "Visualization", 
            description: "Business intelligence and dashboarding",
            tools: ["Tableau", "Power BI", "Looker", "Metabase"] 
          },
          { 
            name: "Data Warehouses", 
            description: "Scalable cloud data storage",
            tools: ["Snowflake", "BigQuery", "Redshift", "Databricks"] 
          },
          { 
            name: "Data Pipelines", 
            description: "ETL/ELT and orchestration",
            tools: ["Apache Airflow", "dbt", "Fivetran", "Prefect"] 
          },
          { 
            name: "Streaming", 
            description: "Real-time data processing",
            tools: ["Apache Kafka", "Flink", "Spark Streaming", "Kinesis"] 
          },
          { 
            name: "Databases", 
            description: "Transactional and analytical storage",
            tools: ["PostgreSQL", "MongoDB", "ClickHouse", "TimescaleDB"] 
          },
          { 
            name: "ML/AI", 
            description: "Predictive and advanced analytics",
            tools: ["Python", "TensorFlow", "scikit-learn", "MLflow"] 
          },
        ],
        checkColor: "text-emerald-500",
      }}
      industries={{
        badge: { icon: TrendingUp, text: "Industries", color: "emerald" },
        title: "Data Impact Across Industries",
        subtitle: "Empowering data-driven decisions in every sector.",
        items: [
          { name: "Retail & E-commerce", icon: ShoppingCart, projects: 45, percentage: 92 },
          { name: "Financial Services", icon: Landmark, projects: 38, percentage: 88 },
          { name: "Healthcare", icon: HeartPulse, projects: 32, percentage: 85 },
          { name: "Manufacturing", icon: Factory, projects: 28, percentage: 82 },
        ],
        progressGradient: "from-emerald-500 to-teal-500",
        progressIconColor: "text-emerald-500",
        statBoxes: [
          {
            icon: BarChart,
            value: "500+",
            label: "Dashboards",
            gradient: "from-emerald-500 to-teal-500",
            iconColor: "text-white",
          },
          {
            icon: Database,
            value: "50+",
            label: "Data Sources",
            gradient: "from-blue-500 to-cyan-500",
            iconColor: "text-white",
          },
          {
            icon: Zap,
            value: "10x",
            label: "Speed",
            gradient: "from-violet-500 to-purple-500",
            iconColor: "text-white",
          },
          {
            icon: Award,
            value: "98%",
            label: "Satisfaction",
            gradient: "from-orange-500 to-red-500",
            iconColor: "text-white",
          },
        ],
      }}
      cta={{
        title: "Ready to Unlock Your Data's Potential?",
        description: "Let's build a data platform that turns your raw data into competitive advantage.",
        buttonText: "Start Data Transformation",
        buttonOnClick: () => navigate('/contact'),
        gradient: "from-emerald-600 via-teal-600 to-cyan-600",
        buttonTextColor: "emerald",
      }}
    >
      {/* Double Diamond Process */}
      <DoubleDiamondProcess />

      {/* Data Capabilities Section */}
      <CapabilitiesGrid
        badge={{ icon: Layers, text: "Data & Analytics Capabilities", color: "emerald" }}
        title="Everything You Need for"
        titleHighlight="Data-Driven Success"
        subtitle="From raw data to executive insights — we build complete data platforms that empower your entire organization to make better decisions."
        items={dataCapabilities}
        hoverColor="emerald"
      />

      {/* Data Impact Section */}
      <BusinessImpactSection
        badge={{ icon: TrendingUp, text: "Business Impact" }}
        title="Why Invest in"
        titleHighlight="Data & Analytics?"
        subtitle="Organizations with strong data cultures outperform competitors. Here's what data transformation delivers."
        items={dataImpact}
        accentColor="teal"
      />

      {/* Why Choose Us Section */}
      <WhyChooseUsSection
        title="Why Choose Us for Data & Analytics?"
        subtitle="We don't just build dashboards — we build data cultures that last."
        stats={[
          { value: "500+", label: "Dashboards built" },
          { value: "Full Stack", label: "Pipelines to BI" },
          { value: "Training", label: "Included always" },
          { value: "98%", label: "Client satisfaction" },
        ]}
        accentColor="emerald"
        cta={{
          text: "Need cloud infrastructure to power your data platform?",
          link: "/services/cloud-solutions",
          linkText: "Check out our Cloud Solutions",
        }}
      />

      {/* FAQ Section */}
      <FAQSection
        badge={{ icon: HelpCircle, text: "FAQ", color: "emerald" }}
        title="Frequently Asked Questions"
        subtitle="Everything you need to know about our data & analytics services."
        items={dataFaqItems}
        accentColor="emerald"
        contactCTAText="Still have questions? We're here to help!"
        contactCTAButtonText="Start Data Transformation"
        onContactClick={() => navigate('/contact')}
      />
    </ServicePageTemplate>
  );
};

export default DataVisualisation;